import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { SorareUser } from '@sorare/core/src/components/user/SorareUser';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import { TokenOwner } from 'components/TokenPreview/TokenOwner';
import { AuctionBidsInfo } from 'components/auction/Auction/AuctionDetails/AuctionBidsInfo';
import { CardBonus } from 'components/card/CardBonus';
import { CollectionPrediction } from 'components/card/CollectionPrediction';
import { useGetAuctionDetails } from 'hooks/offers/useGetAuctionDetails';
import { useGetTokenSingleSaleDetails } from 'hooks/offers/useGetTokenSingleSaleDetails';
import { useIsPrimaryBuyLive } from 'hooks/primaryBuy/useIsPrimaryBuyLive';

import { Price } from '../Price';
import type { PlayerDrawerTokenDetails_anyCard } from './__generated__/index.graphql';

type Props = {
  anyCard: PlayerDrawerTokenDetails_anyCard;
};
const Root = styled(Vertical)`
  flex-grow: 0;
  max-width: calc(100% - calc(7 * var(--unit)));
`;
const Line = styled(Horizontal)`
  align-items: center;
  flex-wrap: wrap;
`;

export const Details = ({ anyCard }: Props) => {
  const auction = useGetAuctionDetails(anyCard.latestEnglishAuction);
  const offer = useGetTokenSingleSaleDetails(anyCard);
  const primaryBuy = useIsPrimaryBuyLive(anyCard);
  const { serialNumber, supply } = anyCard;

  const price = (() => {
    if (primaryBuy) return primaryBuy.price;
    if (offer) return offer.price;
    if (auction) return auction.price;
    if (anyCard?.tokenOwner?.amounts) return anyCard.tokenOwner.amounts;
    return null;
  })();

  const owner = (() => {
    if (primaryBuy) return <SorareUser />;
    if (auction) return null;
    return <TokenOwner card={anyCard} withAvatar />;
  })();

  const timeLeft = offer && <TimeLeft time={offer.endDate} />;

  const bids = (() => {
    if (auction && anyCard?.latestEnglishAuction)
      return <AuctionBidsInfo auction={anyCard.latestEnglishAuction} />;
    return null;
  })();

  return (
    <Root gap={0.5}>
      {price && (
        <Price canDiscount={!!primaryBuy} price={price} sport={anyCard.sport} />
      )}
      <LabelS color="var(--c-nd-600)" as="span">
        <Line gap={0.5}>
          {serialNumber}/{supply}
          {owner ? <> • {owner}</> : ''}
          {timeLeft ? <> • {timeLeft}</> : ''}
          {bids ? <> • {bids}</> : ''}
        </Line>
      </LabelS>
      <Line>
        <CardBonus card={anyCard} withTransferMalus={!!offer} />
        <CollectionPrediction card={anyCard} />
      </Line>
    </Root>
  );
};

Details.fragments = {
  anyCard: gql`
    fragment PlayerDrawerTokenDetails_anyCard on AnyCardInterface {
      slug
      serialNumber
      supply
      sport
      tokenOwner {
        id
        amounts {
          ...MonetaryAmountFragment_monetaryAmount
        }
      }
      latestEnglishAuction {
        id
        ...useGetAuctionDetails_auction
        ...AuctionBidsInfo_auction
      }
      ...CardBonus_anyCard
      ...useGetTokenSingleSaleDetails_anyCard
      ...useIsPrimaryBuyLive_anyCard
      ...TokenOwner_anyCard
      ...CollectionPrediction_anyCard
    }
    ${CardBonus.fragments.anyCard}
    ${useGetAuctionDetails.fragments.auction}
    ${useGetTokenSingleSaleDetails.fragments.anyCard}
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${TokenOwner.fragments.anyCard}
    ${AuctionBidsInfo.fragments.auction}
    ${CollectionPrediction.fragments.anyCard}
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<PlayerDrawerTokenDetails_anyCard>,
};
